// A catalogue game's media (spec/14 §6, ADR-0028; F-CAT-01, F-CAT-07, F-KIO-01): the wide **hero** art, the
// transparent **logo** and the **trailers** the kiosk shows. One dialog per game, opened from its catalogue
// card, like the launch recipes.
//
//  - **Hero / Logo.** The current art (served from our media CDN), and a grid of candidates from SteamGridDB
//    and IGDB (heroes only), each with its source. "Use this" sends `PUT …/art/{kind}`: the server copies the
//    image into our storage and answers the game with its new URL. A source that is not configured is named
//    above the grid; with none at all the server answers `503 art-source-unavailable`. "Clear" removes it
//    (the default-art job then leaves it empty). Logos sit on a checkerboard so their transparency shows.
//  - **Trailers.** The game's uploads with their status, an upload (reserve → signed PUT with progress →
//    complete, `trailer-upload.ts`), remove, and a "Kiosk loop" switch per ready trailer: the lock screen plays
//    the ones switched on, in turn (up to five per game, newest first).
//
//  - **Icon** (an app's only media, spec/14 §6). The square kiosk tile: SteamGridDB icon candidates, or an
//    uploaded PNG/WebP (`icon-upload.ts`). An app's dialog shows just this tab.
//
// Candidate thumbnails are the sources' own CDNs — HQ's preview only; nothing a player sees loads them.

import type { ArtCandidate, ArtKind, Game, GameTrailer } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Badge, Button, cx, Dialog, Skeleton, Switch, Tabs, TextField, useToast } from '@clavian/ui-web';
import { useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent, type ReactNode } from 'react';

import { useApi } from '../../api/client';
import { PROBLEM, problemCode } from '../../api/problems';
import { useProblem } from '../../api/use-problem';
import { Icon } from '../../shell/nav-icons';
import { Notice } from '../../ui';

import { formatIconCap, ICON_MAX_BYTES, IconUploadError, uploadAppIcon } from './icon-upload';
import { catalogueKeys, useArtCandidates, useClearGameArt, useDeleteTrailer, useGameTrailers, useSetGameArt, useSetTrailerInLoop } from './queries';
import { TRAILER_MAX_BYTES, TrailerUploadError, uploadTrailer } from './trailer-upload';
import { UploadDrop } from './UploadDrop';

import './media.css';

/** The problem an art source that is not configured (or is down) answers with. */

type MediaTab = 'hero' | 'logo' | 'icon' | 'trailers';

export function MediaDialog({ game, open, onOpenChange }: { readonly game: Game; readonly open: boolean; readonly onOpenChange: (open: boolean) => void }): ReactNode {
  const { t } = useTranslation('hq');
  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="xl" title={t('catalogue.media.title')} description={game.name}>
      {open ? <MediaBody game={game} /> : null}
    </Dialog>
  );
}

function MediaBody({ game }: { readonly game: Game }) {
  const { t } = useTranslation('hq');
  const isApp = game.kind === 'app';
  const [tab, setTab] = useState<MediaTab>(isApp ? 'icon' : 'hero');
  // The dialog shows the art the last pick answered with at once, before the list refetch lands.
  const [current, setCurrent] = useState<Game>(game);

  if (isApp) {
    // An app has one piece of media: its square icon (spec/14 §6).
    return (
      <div className="console-form console-media">
        <p className="console-muted">{t('catalogue.media.iconIntro')}</p>
        <ArtPanel game={current} kind="icon" active onChange={setCurrent} />
      </div>
    );
  }

  return (
    <div className="console-form console-media">
      <p className="console-muted">{t('catalogue.media.intro')}</p>
      <Tabs
        label={t('catalogue.media.tabs')}
        value={tab}
        onValueChange={(value) => setTab(value as MediaTab)}
        items={[
          { value: 'hero', label: t('catalogue.media.hero'), content: <ArtPanel game={current} kind="hero" active={tab === 'hero'} onChange={setCurrent} /> },
          { value: 'logo', label: t('catalogue.media.logo'), content: <ArtPanel game={current} kind="logo" active={tab === 'logo'} onChange={setCurrent} /> },
          { value: 'trailers', label: t('catalogue.media.trailers'), content: <TrailersPanel game={current} active={tab === 'trailers'} /> },
        ]}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// An app's icon upload (spec/14 §6)
// ---------------------------------------------------------------------------------------------

function IconUpload({ game, onChange }: { readonly game: Game; readonly onChange: (game: Game) => void }) {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const { show } = useToast();
  const api = useApi();
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | undefined>(undefined);
  const cap = formatIconCap(ICON_MAX_BYTES);

  const upload = async (picked: File) => {
    if (progress !== null) return;
    setFile(picked);
    setProgress(0);
    setError(undefined);
    try {
      const updated = await uploadAppIcon(api, game.id, picked, setProgress);
      onChange(updated);
      void queryClient.invalidateQueries({ queryKey: catalogueKeys.games });
      show({ tone: 'ok', title: t('catalogue.media.iconUpload.uploaded', { game: game.name }) });
    } catch (err) {
      const reason = err instanceof IconUploadError ? err.reason : 'failed';
      setError(
        reason === 'wrongType'
          ? t('catalogue.media.iconUpload.wrongType')
          : reason === 'tooLarge'
            ? t('catalogue.media.iconUpload.tooLarge', { size: cap })
            : problems.text(err, { fallback: t('catalogue.media.iconUpload.failed') }),
      );
    } finally {
      setProgress(null);
    }
  };

  return (
    <section className="console-media-section" aria-labelledby="console-media-upload-title">
      <h3 id="console-media-upload-title" className="console-media-heading">
        {t('catalogue.media.iconUpload.file')}
      </h3>
      <UploadDrop
        label={t('catalogue.apps.sheet.iconFile')}
        title={t('catalogue.media.iconUpload.drop')}
        hint={t('catalogue.media.iconUpload.hint', { size: cap })}
        accept="image/png,image/webp"
        disabled={progress !== null}
        progress={progress}
        progressLabel={file === null ? undefined : t('catalogue.media.iconUpload.uploading', { name: file.name })}
        onFile={(picked) => {
          if (picked !== null) void upload(picked);
        }}
      />
      {error !== undefined ? (
        <Notice tone="danger" role="alert">
          {error}
        </Notice>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------------------------
// Hero and logo
// ---------------------------------------------------------------------------------------------

function ArtPanel({ game, kind, active, onChange }: { readonly game: Game; readonly kind: ArtKind; readonly active: boolean; readonly onChange: (game: Game) => void }) {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const { show } = useToast();
  const candidates = useArtCandidates(game.id, kind, active);
  const pick = useSetGameArt();
  const clear = useClearGameArt();
  const [pickingId, setPickingId] = useState<string | null>(null);
  // The candidate this dialog last put in use — the current art is our CDN's copy, so its URL never matches.
  const [pickedId, setPickedId] = useState<string | null>(null);
  const kindName = t(`catalogue.media.${kind}`);
  const currentUrl = kind === 'hero' ? game.heroUrl : kind === 'logo' ? game.logoUrl : game.iconUrl;
  const currentSource = kind === 'hero' ? game.heroSource : kind === 'logo' ? game.logoSource : game.iconSource;

  const choose = (candidate: ArtCandidate) => {
    if (pick.isPending) return;
    const id = `${candidate.source}:${candidate.sourceId}`;
    setPickingId(id);
    pick.mutate(
      { id: game.id, kind, body: { source: candidate.source, sourceId: candidate.sourceId } },
      {
        onSuccess: (updated) => {
          onChange(updated);
          setPickedId(id);
          show({ tone: 'ok', title: t('catalogue.media.saved', { kind: kindName, game: game.name }) });
        },
        onSettled: () => setPickingId(null),
      },
    );
  };

  const retry = (
    <Button variant="secondary" size="sm" icon={<Icon name="refresh" />} busy={candidates.isFetching} onClick={() => void candidates.refetch()}>
      {t('catalogue.media.retry')}
    </Button>
  );

  let grid: ReactNode;
  let count: number | null = null;
  if (candidates.data === undefined) {
    if (problemCode(candidates.error) === PROBLEM.artSourceUnavailable) {
      grid = (
        <div className="console-media-problem">
          <Notice tone="warn">{t('catalogue.media.allUnavailable')}</Notice>
          {retry}
        </div>
      );
    } else if (candidates.isError) {
      grid = (
        <div className="console-media-problem">
          <Notice tone="danger" role="alert">
            {problems.text(candidates.error, { fallback: t('catalogue.media.failed') })}
          </Notice>
          {retry}
        </div>
      );
    } else {
      grid = (
        <div className={`console-media-grid console-media-grid--${kind}`} role="status" aria-busy="true" aria-label={t('catalogue.media.loading')}>
          {Array.from({ length: kind === 'icon' ? 8 : 4 }, (_, index) => (
            <div key={index} className="console-media-candidate" aria-hidden="true">
              <Skeleton className={`console-media-frame console-media-frame--${kind}`} />
              <Skeleton shape="text" width="60%" />
            </div>
          ))}
        </div>
      );
    }
  } else {
    const { data, unavailableSources } = candidates.data;
    count = data.length;
    grid = (
      <>
        {unavailableSources.length > 0 ? (
          <div className="console-media-problem">
            <Notice tone="warn">{t('catalogue.media.unavailable', { sources: unavailableSources.map((source) => t(`catalogue.media.source.${source}`)).join(', ') })}</Notice>
            {retry}
          </div>
        ) : null}
        {data.length === 0 ? (
          <p className="console-muted">{t('catalogue.media.noCandidates')}</p>
        ) : (
          <ul className={`console-media-grid console-media-grid--${kind}`} aria-label={t('catalogue.media.candidatesFor', { kind: kindName, game: game.name })}>
            {data.map((candidate, index) => {
              const source = t(`catalogue.media.source.${candidate.source}`);
              const id = `${candidate.source}:${candidate.sourceId}`;
              const picked = pickedId === id;
              return (
                <li key={id} className={cx('console-media-candidate', 'console-media-candidate--pickable', picked && 'console-media-candidate--picked')}>
                  <span className={`console-media-frame console-media-frame--${kind}`}>
                    <img src={candidate.url} alt={t('catalogue.media.candidateAlt', { source, kind: kindName, index: index + 1 })} loading="lazy" />
                  </span>
                  <span className="console-media-candidate__meta">
                    <Badge tone="neutral">{source}</Badge>
                    {candidate.width !== null && candidate.height !== null ? (
                      <span className="console-media-candidate__size">{t('catalogue.media.size', { width: candidate.width, height: candidate.height })}</span>
                    ) : null}
                  </span>
                  {picked ? (
                    <Badge tone="ok">
                      <Icon name="check" /> {t('catalogue.media.picked')}
                    </Badge>
                  ) : (
                    <Button
                      variant="secondary"
                      size="sm"
                      className="console-media-candidate__use"
                      busy={pickingId === id}
                      label={t('catalogue.media.pickLabel', { source, kind: kindName, index: index + 1 })}
                      onClick={() => choose(candidate)}
                    >
                      {t('catalogue.media.pick')}
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </>
    );
  }

  return (
    <div className="console-media-panel">
      <section className="console-media-section" aria-labelledby={`console-media-current-${kind}`}>
        <h3 id={`console-media-current-${kind}`} className="console-media-heading">
          {t('catalogue.media.current')}
        </h3>
        {currentUrl === null ? (
          <div className="console-media-current__row">
            <span className={`console-media-frame console-media-frame--${kind} console-media-frame--current console-media-frame--empty`} aria-hidden="true">
              <Icon name="image" />
            </span>
            <span className="console-media-current__facts">
              <span className="console-media-current__title">{t('catalogue.media.none')}</span>
              <span className="console-muted">{t('catalogue.media.noneHint')}</span>
            </span>
          </div>
        ) : (
          <div className="console-media-current__row">
            <span className={`console-media-frame console-media-frame--${kind} console-media-frame--current`}>
              <img src={currentUrl} alt={t('catalogue.media.currentAlt', { kind: kindName, game: game.name })} />
            </span>
            <span className="console-media-current__facts">
              {currentSource === 'steamgriddb' || currentSource === 'igdb' || currentSource === 'upload' ? (
                <Badge tone="neutral">{t(`catalogue.media.source.${currentSource}`)}</Badge>
              ) : null}
              <Button
                variant="ghost"
                size="sm"
                icon={<Icon name="close" />}
                busy={clear.isPending}
                label={t('catalogue.media.clearLabel', { kind: kindName })}
                onClick={() =>
                  clear.mutate(
                    { id: game.id, kind },
                    {
                      onSuccess: (updated) => {
                        onChange(updated);
                        setPickedId(null);
                        show({ tone: 'ok', title: t('catalogue.media.cleared', { kind: kindName, game: game.name }) });
                      },
                    },
                  )
                }
              >
                {t('catalogue.media.clear')}
              </Button>
            </span>
          </div>
        )}
      </section>
      {kind === 'icon' ? <IconUpload game={game} onChange={onChange} /> : null}
      {pick.isError ? (
        <Notice tone="danger" role="alert">
          {problemCode(pick.error) === PROBLEM.artSourceUnavailable ? t('catalogue.media.allUnavailable') : problems.text(pick.error, { fallback: t('catalogue.media.pickFailed') })}
        </Notice>
      ) : null}
      <section className="console-media-section" aria-labelledby={`console-media-candidates-${kind}`}>
        <div className="console-media-subhead">
          <h3 id={`console-media-candidates-${kind}`} className="console-media-heading">
            {t('catalogue.media.candidates')}
          </h3>
          {count === null || count === 0 ? null : <span className="console-media-subhead__count">{t('catalogue.media.candidateCount', { count })}</span>}
        </div>
        {grid}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Trailers
// ---------------------------------------------------------------------------------------------

const megabytes = (bytes: number) => (bytes / (1024 * 1024)).toFixed(bytes >= 10 * 1024 * 1024 ? 0 : 1);

function TrailersPanel({ game, active }: { readonly game: Game; readonly active: boolean }) {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const { show } = useToast();
  const api = useApi();
  const queryClient = useQueryClient();
  const trailers = useGameTrailers(game.id, active);
  const remove = useDeleteTrailer();
  const loop = useSetTrailerInLoop();
  const [title, setTitle] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | undefined>(undefined);
  const maxSize = t('catalogue.media.trailer.megabytes', { value: megabytes(TRAILER_MAX_BYTES) });

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (file === null || progress !== null) return;
    setError(undefined);
    setProgress(0);
    try {
      await uploadTrailer(api, game.id, title.trim() === '' ? t('catalogue.media.trailer.titleDefault') : title.trim(), file, setProgress);
      show({ tone: 'ok', title: t('catalogue.media.trailer.uploaded', { game: game.name }) });
      setTitle('');
      setFile(null);
    } catch (failure) {
      const reason = failure instanceof TrailerUploadError ? failure.reason : 'failed';
      setError(
        reason === 'wrongType'
          ? t('catalogue.media.trailer.wrongType')
          : reason === 'tooLarge'
            ? t('catalogue.media.trailer.tooLarge', { size: maxSize })
            : problems.text(failure, { fallback: t('catalogue.media.trailer.uploadFailed') }),
      );
    } finally {
      setProgress(null);
      void queryClient.invalidateQueries({ queryKey: catalogueKeys.trailers(game.id) });
    }
  };

  let list: ReactNode;
  if (trailers.data === undefined) {
    list = trailers.isError ? (
      <Notice tone="danger" role="alert">
        {problems.text(trailers.error, { fallback: t('catalogue.media.trailer.failedToLoad') })}
      </Notice>
    ) : (
      <div className="console-media-trailers" role="status" aria-busy="true" aria-label={t('catalogue.media.loading')}>
        <Skeleton height="2.75rem" />
        <Skeleton height="2.75rem" />
      </div>
    );
  } else if (trailers.data.length === 0) {
    list = <p className="console-muted">{t('catalogue.media.trailer.empty')}</p>;
  } else {
    list = (
      <ul className="console-media-trailers" aria-label={t('catalogue.media.trailer.list', { game: game.name })}>
        {trailers.data.map((trailer) => (
          <TrailerRow
            key={trailer.id}
            trailer={trailer}
            busy={remove.isPending && remove.variables?.trailerId === trailer.id}
            loopBusy={loop.isPending && loop.variables?.trailerId === trailer.id}
            onRemove={() => remove.mutate({ id: game.id, trailerId: trailer.id })}
            onLoopChange={(inKioskLoop) => loop.mutate({ id: game.id, trailerId: trailer.id, inKioskLoop })}
          />
        ))}
      </ul>
    );
  }

  return (
    <div className="console-media-panel">
      <p className="console-muted">{t('catalogue.media.trailer.intro', { size: maxSize })}</p>
      {remove.isError ? (
        <Notice tone="danger" role="alert">
          {problems.text(remove.error, { fallback: t('catalogue.media.trailer.removeFailed') })}
        </Notice>
      ) : null}
      {loop.isError ? (
        <Notice tone="danger" role="alert">
          {problems.text(loop.error, { fallback: t('catalogue.media.trailer.loopFailed') })}
        </Notice>
      ) : null}
      {list}
      <form className="console-media-upload" onSubmit={(event) => void submit(event)} noValidate>
        <TextField label={t('catalogue.media.trailer.titleLabel')} showOptional value={title} maxLength={120} onChange={(event) => setTitle(event.target.value)} />
        <UploadDrop
          label={t('catalogue.media.trailer.file')}
          title={t('catalogue.media.trailer.drop')}
          hint={t('catalogue.media.trailer.dropHint', { size: maxSize })}
          accept="video/mp4,video/webm"
          disabled={progress !== null}
          fileName={file === null ? null : `${file.name} · ${t('catalogue.media.trailer.megabytes', { value: megabytes(file.size) })}`}
          progress={progress}
          progressLabel={file === null ? undefined : t('catalogue.media.trailer.uploading', { name: file.name })}
          onFile={(picked) => {
            setError(undefined);
            setFile(picked);
          }}
        />
        {error !== undefined ? (
          <Notice tone="danger" role="alert">
            {error}
          </Notice>
        ) : null}
        <div className="console-form__actions">
          <Button type="submit" variant="primary" icon={<Icon name="upload" />} busy={progress !== null} disabled={file === null}>
            {t('catalogue.media.trailer.upload')}
          </Button>
        </div>
      </form>
    </div>
  );
}

function TrailerRow({
  trailer,
  busy,
  loopBusy,
  onRemove,
  onLoopChange,
}: {
  readonly trailer: GameTrailer;
  readonly busy: boolean;
  readonly loopBusy: boolean;
  readonly onRemove: () => void;
  readonly onLoopChange: (inKioskLoop: boolean) => void;
}) {
  const { t } = useTranslation('hq');
  const tone = trailer.status === 'ready' ? 'ok' : trailer.status === 'failed' ? 'danger' : 'neutral';
  return (
    <li className="console-media-trailer">
      <span className="console-media-trailer__title">{trailer.title}</span>
      <Badge tone={tone}>{t(`catalogue.media.trailer.status.${trailer.status}`)}</Badge>
      <span className="console-muted">{t('catalogue.media.trailer.megabytes', { value: megabytes(trailer.bytes) })}</span>
      {trailer.status === 'ready' ? (
        <Switch
          label={
            <>
              <span aria-hidden="true">{t('catalogue.media.trailer.loop')}</span>
              <span className="clav-visually-hidden">{t('catalogue.media.trailer.loopLabel', { title: trailer.title })}</span>
            </>
          }
          checked={trailer.inKioskLoop}
          disabled={loopBusy}
          onCheckedChange={onLoopChange}
        />
      ) : null}
      <Button variant="ghost" size="sm" busy={busy} label={t('catalogue.media.trailer.removeLabel', { title: trailer.title })} onClick={onRemove}>
        {t('catalogue.media.trailer.remove')}
      </Button>
    </li>
  );
}
